import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Paper,
  Container,
  Typography,
  IconButton,
  CircularProgress,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Tooltip,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Edit,
  Close,
  DeleteOutline,
  AccountTree,
  History,
  Add,
  Publish,
  Undo,
  Block,
  Visibility,
  AttachFile,
  Download,
  InsertDriveFile,
} from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import { FaqButton } from "../components/FaqButton";
import AddButton from "../components/AddButton";
import ClearFiltersButton from "../components/ClearFiltersButton";
import ResultCount from "../components/ResultCount";
import StandardPagination, { ITENS_POR_PAGINA_PADRAO } from "../components/Pagination/StandardPagination";
import ConfirmModal from "../components/modals/ConfirmModal";
import { Toast } from "../components/Toast";
import SavingOverlay from "../components/SavingOverlay";
import { useToast } from "../hooks/useToast";
import { useRouter } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { colors, typography, pageHeader } from "../theme/designSystem";
import {
  workflowsService,
  workflowCatalogService,
  workflowErrorMessage,
  downloadBlob,
  formatFileSize,
  WorkflowAttachment,
  WorkflowListItem,
  WorkflowProfileOption,
  WorkflowVersionSummary,
  WorkflowTargetKind,
  ATTACHMENT_ACCEPT,
  ATTACHMENT_MAX_BYTES,
  ATTACHMENT_MAX_COUNT,
  CATEGORY_SUGGESTIONS,
  TARGET_KIND_LABELS,
  VERSION_STATUS_COLORS,
  VERSION_STATUS_LABELS,
} from "../services/workflowsService";

const formatDate = (value: string | null): string =>
  value ? new Date(value).toLocaleDateString("pt-BR") : "-";

interface FormState {
  id: string | null; // null = criação
  name: string;
  purpose: string;
  category: string;
  groupName: string;
  targetKind: WorkflowTargetKind;
  videoUrl: string;
  visibilityProfileIds: string[];
}

const blankForm = (): FormState => ({
  id: null,
  name: "",
  purpose: "",
  category: "",
  groupName: "",
  targetKind: "patient",
  videoUrl: "",
  visibilityProfileIds: [],
});

// Ação destrutiva/irreversível confirmada no ConfirmModal: guardar a execução no estado evita
// um modal por tipo de ação sobre versão.
interface PendingAction {
  title: string;
  message: string;
  confirmText: string;
  run: () => Promise<void>;
}

const Workflows: React.FC = () => {
  const { navigateTo } = useRouter();
  const { user } = useAuth();
  const { toast, showToast, hideToast } = useToast();
  const entityId = user?.entityId || "";
  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down("sm"));

  const [workflows, setWorkflows] = useState<WorkflowListItem[]>([]);
  const [profiles, setProfiles] = useState<WorkflowProfileOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [attachments, setAttachments] = useState<WorkflowAttachment[]>([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [groupFilter, setGroupFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState<"active" | "inactive" | "all">("active");

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(ITENS_POR_PAGINA_PADRAO);

  const [form, setForm] = useState<FormState | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<WorkflowListItem | null>(null);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);

  const [versionsOf, setVersionsOf] = useState<WorkflowListItem | null>(null);
  const [versions, setVersions] = useState<WorkflowVersionSummary[] | null>(null);
  const [loadingVersions, setLoadingVersions] = useState(false);

  const load = async () => {
    if (!entityId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [items, profileOptions] = await Promise.all([
        workflowsService.getByEntity(entityId, true),
        workflowsService.getProfiles().catch(() => [] as WorkflowProfileOption[]),
      ]);
      setWorkflows(items);
      setProfiles(profileOptions);
    } catch (e) {
      showToast(workflowErrorMessage(e, "Erro ao carregar os fluxos de trabalho."), "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Clinic4Us - Fluxos de trabalho";
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  const categories = useMemo(
    () =>
      Array.from(new Set(workflows.map((w) => (w.category || "").trim()).filter(Boolean))).sort((a, b) =>
        a.localeCompare(b, "pt-BR")
      ),
    [workflows]
  );

  /** Grupos já usados pela entidade — viram sugestão no cadastro e opção no filtro. */
  const groupOptions = useMemo(
    () =>
      Array.from(new Set(workflows.map((w) => (w.groupName || "").trim()).filter(Boolean))).sort(
        (a, b) => a.localeCompare(b, "pt-BR")
      ),
    [workflows]
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return workflows
      .filter((w) => !term || w.name.toLowerCase().includes(term))
      .filter((w) => categoryFilter === "all" || (w.category || "").trim() === categoryFilter)
      .filter((w) => groupFilter === "all" || (w.groupName || "").trim() === groupFilter)
      .filter((w) =>
        statusFilter === "all" ? true : statusFilter === "active" ? w.active : !w.active
      )
      // Grupo primeiro (sem grupo vai ao fim) e, dentro dele, nome — a mesma ordem do Playbook.
      .sort((a, b) => {
        const ga = (a.groupName || "").trim();
        const gb = (b.groupName || "").trim();
        if (ga !== gb) {
          if (!ga) return 1;
          if (!gb) return -1;
          return ga.localeCompare(gb, "pt-BR");
        }
        return a.name.localeCompare(b.name, "pt-BR");
      });
  }, [workflows, search, categoryFilter, groupFilter, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));
  const paged = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  // Mexeu no filtro, volta para a primeira pagina: senao a lista aparece vazia sem motivo
  useEffect(() => {
    setCurrentPage(1);
  }, [search, groupFilter, statusFilter]);

  const clearFilters = () => {
    setSearch("");
    setGroupFilter("all");
    setStatusFilter("active");
  };

  const hasFilterChanges =
    search !== "" || groupFilter !== "all" || statusFilter !== "active";

  const openCreate = () => {
    setAttachments([]);
    setForm(blankForm());
  };

  const openEdit = (workflow: WorkflowListItem) => {
    setForm({
      id: workflow.id,
      name: workflow.name,
      purpose: workflow.purpose || "",
      category: workflow.category || "",
      groupName: workflow.groupName || "",
      targetKind: workflow.targetKind,
      videoUrl: workflow.videoUrl || "",
      visibilityProfileIds: workflow.visibilityProfileIds || [],
    });
    loadAttachments(workflow.id);
  };

  const loadAttachments = async (workflowId: string) => {
    setAttachments([]);
    setLoadingAttachments(true);
    try {
      setAttachments(await workflowsService.getAttachments(workflowId));
    } catch (e) {
      showToast(workflowErrorMessage(e, "Erro ao carregar os anexos do fluxo."), "error");
    } finally {
      setLoadingAttachments(false);
    }
  };

  const uploadAttachment = async (file: File | null) => {
    if (!file || !form?.id) return;
    if (file.size > ATTACHMENT_MAX_BYTES) {
      showToast("O arquivo passa de 10 MB. Envie uma versão menor.", "error");
      return;
    }
    setSaving(true);
    try {
      await workflowsService.uploadAttachment(form.id, file);
      showToast("Anexo enviado.", "success");
      await loadAttachments(form.id);
    } catch (e) {
      showToast(workflowErrorMessage(e, "Erro ao enviar o anexo."), "error");
    } finally {
      setSaving(false);
    }
  };

  const downloadAttachment = async (attachment: WorkflowAttachment) => {
    try {
      const blob = await workflowCatalogService.downloadAttachment(attachment.id);
      downloadBlob(blob, attachment.fileName);
    } catch (e) {
      showToast(workflowErrorMessage(e, "Erro ao baixar o anexo."), "error");
    }
  };

  const removeAttachment = async (attachment: WorkflowAttachment) => {
    if (!form?.id) return;
    setSaving(true);
    try {
      await workflowsService.removeAttachment(attachment.id);
      showToast("Anexo excluído.", "success");
      await loadAttachments(form.id);
    } catch (e) {
      showToast(workflowErrorMessage(e, "Erro ao excluir o anexo."), "error");
    } finally {
      setSaving(false);
    }
  };

  const goToEditor = (versionId: string) => navigateTo("workflow-editor", { versionId });

  const save = async () => {
    if (!form) return;
    if (!form.name.trim()) {
      showToast("Informe o nome do fluxo.", "error");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        purpose: form.purpose.trim() || null,
        category: form.category.trim() || null,
        groupName: form.groupName.trim() || null,
        targetKind: form.targetKind,
        videoUrl: form.videoUrl.trim() || null,
        visibilityProfileIds: form.visibilityProfileIds,
      };
      if (form.id) {
        await workflowsService.update(form.id, payload);
        setForm(null);
        showToast("Fluxo atualizado.", "success");
        await load();
        return;
      }

      const newId = await workflowsService.create(entityId, payload);
      setForm(null);
      // O fluxo nasce com a versão 1 em rascunho: leva direto ao editor para desenhar as etapas.
      const created = await workflowsService.getVersions(newId);
      if (created.length > 0) {
        goToEditor(created[0].id);
        return;
      }
      showToast("Fluxo criado.", "success");
      await load();
    } catch (e) {
      showToast(workflowErrorMessage(e, "Erro ao salvar o fluxo."), "error");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (workflow: WorkflowListItem) => {
    try {
      await workflowsService.setActive(workflow.id, !workflow.active);
      await load();
    } catch (e) {
      showToast(workflowErrorMessage(e, "Erro ao alterar a situação do fluxo."), "error");
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await workflowsService.remove(deleteTarget.id);
      setDeleteTarget(null);
      showToast("Fluxo excluído.", "success");
      await load();
    } catch (e) {
      showToast(workflowErrorMessage(e, "Erro ao excluir o fluxo."), "error");
    } finally {
      setSaving(false);
    }
  };

  const openVersions = async (workflow: WorkflowListItem) => {
    setVersionsOf(workflow);
    setVersions(null);
    setLoadingVersions(true);
    try {
      setVersions(await workflowsService.getVersions(workflow.id));
    } catch (e) {
      showToast(workflowErrorMessage(e, "Erro ao carregar as versões."), "error");
      setVersions([]);
    } finally {
      setLoadingVersions(false);
    }
  };

  const reloadVersions = async (workflowId: string) => {
    try {
      setVersions(await workflowsService.getVersions(workflowId));
    } catch {
      // a lista principal já recarregou; o erro da ação em si já foi reportado
    }
  };

  // Toda ação sobre versão recarrega a lista de versões e a lista de fluxos (mudam vigente/rascunho).
  const runVersionAction = async (action: () => Promise<void>, successMessage: string) => {
    if (!versionsOf) return;
    setSaving(true);
    try {
      await action();
      showToast(successMessage, "success");
      await reloadVersions(versionsOf.id);
      await load();
    } catch (e) {
      showToast(workflowErrorMessage(e, "Erro ao executar a ação na versão."), "error");
    } finally {
      setSaving(false);
    }
  };

  const createVersion = async () => {
    if (!versionsOf) return;
    setSaving(true);
    try {
      // Clona da vigente quando existe; sem versão publicada, abre rascunho da última que houver.
      const source =
        versionsOf.currentVersionId ||
        (versions && versions.length > 0 ? versions[0].id : null);
      const newId = await workflowsService.createVersion(versionsOf.id, source, null);
      goToEditor(newId);
    } catch (e) {
      showToast(workflowErrorMessage(e, "Erro ao criar a nova versão."), "error");
      setSaving(false);
    }
  };

  const askConfirm = (action: PendingAction) => setPendingAction(action);

  // O modal fica aberto durante a execução para o overlay "Processando" do ConfirmModal aparecer.
  const runPendingAction = async () => {
    if (!pendingAction) return;
    await pendingAction.run();
    setPendingAction(null);
  };

  const hasDraft = versions?.some((v) => v.status === "draft") ?? false;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={() => navigateTo("dashboard")}
      />

      <Box
        component="main"
        className="c4u-main"
      >
        <Container maxWidth={false} disableGutters sx={pageHeader.container}>
          <Box className="c4u-page-header c4u-page-header--in-panel">
            <Box className="c4u-page-header__texts">
              <Typography variant="h4" className="c4u-page-header__title">
                Fluxos de trabalho
              </Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                Processos configuráveis da clínica (jornada do paciente, rotinas administrativas e
                financeiras). Cada fluxo tem versões: a publicada é imutável — para mudar, clone-a em uma
                versão nova.
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexShrink: 0,
                justifyContent: "flex-end",
              }}
            >
              <FaqButton />
            </Box>
          </Box>

          <div className="c4u-panel">
            <div className="c4u-filters__row">
              <div className="c4u-filters__fields">
              <TextField
                label="Buscar por nome"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                size="small"
                sx={{ flex: "1 1 220px", minWidth: 0 }}
                inputProps={{ maxLength: 120 }}
                InputLabelProps={{ shrink: true }}
                placeholder="ex.: Acolhimento do paciente"
              />
              <FormControl size="small" sx={{ flex: "1 1 160px", minWidth: 140 }}>
                <InputLabel shrink>Setor</InputLabel>
                <Select
                  label="Setor"
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  notched
                  displayEmpty
                >
                  <MenuItem value="all">Todos</MenuItem>
                  {categories.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ flex: "1 1 160px", minWidth: 140 }}>
                <InputLabel shrink>Grupo</InputLabel>
                <Select
                  label="Grupo"
                  value={groupFilter}
                  onChange={(e) => setGroupFilter(e.target.value)}
                  notched
                  displayEmpty
                >
                  <MenuItem value="all">Todos</MenuItem>
                  {groupOptions.map((g) => (
                    <MenuItem key={g} value={g}>
                      {g}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ flex: "1 1 160px", minWidth: 140 }}>
                <InputLabel shrink>Situação</InputLabel>
                <Select
                  label="Situação"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as "active" | "inactive" | "all")}
                  notched
                  displayEmpty
                >
                  <MenuItem value="active">Ativos</MenuItem>
                  <MenuItem value="inactive">Inativos</MenuItem>
                  <MenuItem value="all">Todos</MenuItem>
                </Select>
              </FormControl>
              </div>

              <div className="c4u-filters__actions">
                <ClearFiltersButton onClick={clearFilters} disabled={!hasFilterChanges} />
                <AddButton onClick={openCreate} title="Novo fluxo" />
              </div>
            </div>

            <ResultCount shown={filtered.length} total={workflows.length} />

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress sx={{ color: colors.primary }} />
            </Box>
          ) : filtered.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 6, color: colors.textSecondary }}>
              <AccountTree sx={{ fontSize: 48, color: "#ced4da", mb: 1 }} />
              <Typography variant="body1" sx={{ mb: 0.5, color: colors.textPrimary }}>
                {workflows.length === 0 ? "Nenhum fluxo cadastrado" : "Nenhum fluxo no filtro selecionado"}
              </Typography>
              <Typography variant="body2">
                {workflows.length === 0
                  ? 'Crie o primeiro processo da clínica em "Novo fluxo".'
                  : "Ajuste a busca, o setor ou a situação."}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {paged.map((workflow) => (
                <Paper
                  key={workflow.id}
                  elevation={0}
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: { xs: "stretch", sm: "center" },
                    gap: { xs: 1.5, sm: 2 },
                    p: 1.5,
                    border: "1px solid #e9ecef",
                    borderRadius: "8px",
                    opacity: workflow.active ? 1 : 0.65,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: colors.textPrimary,
                        wordBreak: "break-word",
                        lineHeight: 1.25,
                      }}
                    >
                      {workflow.name}
                    </Typography>
                    {workflow.purpose && (
                      <Typography
                        variant="caption"
                        sx={{ display: "block", color: colors.textSecondary, wordBreak: "break-word" }}
                      >
                        {workflow.purpose}
                      </Typography>
                    )}
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.75 }}>
                      {workflow.category && (
                        <Chip
                          label={workflow.category}
                          size="small"
                          variant="outlined"
                          sx={{ height: 22, fontSize: "0.7rem" }}
                        />
                      )}
                      <Chip
                        label={TARGET_KIND_LABELS[workflow.targetKind] ?? workflow.targetKind}
                        size="small"
                        variant="outlined"
                        sx={{ height: 22, fontSize: "0.7rem" }}
                      />
                      <Chip
                        label={
                          workflow.currentVersionNumber
                            ? `v${workflow.currentVersionNumber} vigente`
                            : "sem versão publicada"
                        }
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: "0.7rem",
                          color: "#fff",
                          bgcolor: workflow.currentVersionNumber ? "#28a745" : "#adb5bd",
                        }}
                      />
                      {workflow.draftVersionId && (
                        <Chip
                          label={`rascunho v${workflow.draftVersionNumber} em edição`}
                          size="small"
                          sx={{ height: 22, fontSize: "0.7rem", color: "#fff", bgcolor: "#fd7e14" }}
                        />
                      )}
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      justifyContent: { xs: "space-between", sm: "flex-end" },
                      flexShrink: 0,
                    }}
                  >
                    <Chip
                      label={workflow.active ? "Ativo" : "Inativo"}
                      size="small"
                      onClick={() => toggleActive(workflow)}
                      title={workflow.active ? "Clique para inativar" : "Clique para ativar"}
                      sx={{
                        color: "white",
                        fontWeight: 600,
                        cursor: "pointer",
                        backgroundColor: workflow.active ? "#28a745" : "#adb5bd",
                      }}
                    />
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => openVersions(workflow)}
                        title={`Versões (${workflow.versionCount})`}
                        sx={{
                          backgroundColor: "#6c757d",
                          color: "white",
                          width: 32,
                          height: 32,
                          "&:hover": { backgroundColor: "#5a6268" },
                        }}
                      >
                        <History sx={{ fontSize: "1rem" }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => openEdit(workflow)}
                        title="Editar dados do fluxo"
                        sx={{
                          backgroundColor: "#03B4C6",
                          color: "white",
                          width: 32,
                          height: 32,
                          "&:hover": { backgroundColor: "#029AAB" },
                        }}
                      >
                        <Edit sx={{ fontSize: "1rem" }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => setDeleteTarget(workflow)}
                        title="Excluir"
                        sx={{
                          backgroundColor: "#dc3545",
                          color: "white",
                          width: 32,
                          height: 32,
                          "&:hover": { backgroundColor: "#c82333" },
                        }}
                      >
                        <DeleteOutline sx={{ fontSize: "1rem" }} />
                      </IconButton>
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}

            {filtered.length > 0 && (
              <StandardPagination
                currentPage={currentPage}
                totalPages={totalPages}
                itemsPerPage={itemsPerPage}
                totalItems={filtered.length}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={(n) => {
                  setItemsPerPage(n);
                  setCurrentPage(1);
                }}
              />
            )}
          </div>
        </Container>
      </Box>

      {/* Dados do fluxo (criar/editar) — o desenho das etapas fica no editor de versão */}
      <Dialog
        open={form !== null}
        onClose={() => setForm(null)}
        maxWidth="sm"
        fullWidth
        fullScreen={fullScreenDialog}
        PaperProps={{
          sx: {
            borderRadius: fullScreenDialog ? 0 : "12px",
            position: "relative",
            overflow: "hidden",
          },
        }}
      >
        <SavingOverlay open={saving} message="Salvando..." />
        <DialogTitle
          sx={{
            backgroundColor: colors.primary,
            color: colors.white,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "1rem 1.25rem",
          }}
        >
          <Typography
            component="h3"
            sx={{ fontSize: "1.1rem", fontWeight: typography.fontWeight.semibold, margin: 0 }}
          >
            {form?.id ? "Editar fluxo" : "Novo fluxo"}
          </Typography>
          <IconButton onClick={() => setForm(null)} sx={{ color: colors.white, padding: "0.25rem" }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "1.25rem !important", paddingTop: "1.5rem !important" }}>
          {form && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Nome do fluxo"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                fullWidth
                size="small"
                required
                inputProps={{ maxLength: 120 }}
                InputLabelProps={{ shrink: true }}
                placeholder="ex.: Acolhimento do novo paciente"
              />
              <TextField
                label="Finalidade"
                value={form.purpose}
                onChange={(e) => setForm({ ...form, purpose: e.target.value })}
                fullWidth
                size="small"
                multiline
                minRows={3}
                inputProps={{ maxLength: 500 }}
                InputLabelProps={{ shrink: true }}
                placeholder="O que este processo resolve e quando ele é usado."
              />
              <Autocomplete
                freeSolo
                options={CATEGORY_SUGGESTIONS}
                value={form.category}
                onChange={(_, value) => setForm({ ...form, category: value ?? "" })}
                onInputChange={(_, value) => setForm({ ...form, category: value })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Setor"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ ...params.inputProps, maxLength: 60 }}
                    placeholder="ex.: Clínico"
                  />
                )}
              />
              <Autocomplete
                freeSolo
                options={groupOptions}
                value={form.groupName}
                onChange={(_, value) => setForm({ ...form, groupName: value ?? "" })}
                onInputChange={(_, value) => setForm({ ...form, groupName: value })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Grupo"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ ...params.inputProps, maxLength: 80 }}
                    placeholder="ex.: Ciclo de vida do paciente"
                    helperText="Reúne na consulta os fluxos que juntos formam um processo maior."
                  />
                )}
              />
              <FormControl size="small" fullWidth>
                <InputLabel shrink>Alvo do fluxo</InputLabel>
                <Select
                  label="Alvo do fluxo"
                  value={form.targetKind}
                  onChange={(e) => setForm({ ...form, targetKind: e.target.value as WorkflowTargetKind })}
                  notched
                >
                  {(Object.keys(TARGET_KIND_LABELS) as WorkflowTargetKind[]).map((k) => (
                    <MenuItem key={k} value={k}>
                      {TARGET_KIND_LABELS[k]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" fullWidth>
                <InputLabel shrink>Quem pode ver</InputLabel>
                <Select
                  label="Quem pode ver"
                  multiple
                  notched
                  displayEmpty
                  value={form.visibilityProfileIds}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      visibilityProfileIds:
                        typeof e.target.value === "string"
                          ? e.target.value.split(",")
                          : e.target.value,
                    })
                  }
                  renderValue={(selected) =>
                    selected.length === 0 ? (
                      <Typography sx={{ fontSize: "0.9rem", color: colors.textMuted }}>
                        Nenhum perfil selecionado
                      </Typography>
                    ) : (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((id) => (
                          <Chip
                            key={id}
                            label={profiles.find((p) => p.id === id)?.name ?? id}
                            size="small"
                            sx={{ height: 22, fontSize: "0.7rem" }}
                          />
                        ))}
                      </Box>
                    )
                  }
                  MenuProps={{ PaperProps: { style: { maxHeight: 280 } } }}
                >
                  {profiles.map((profile) => (
                    <MenuItem key={profile.id} value={profile.id}>
                      {profile.name}
                    </MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" sx={{ color: colors.textSecondary, mt: 0.5 }}>
                  Quem consulta o processo no Playbook. Quem responde por alguma etapa já enxerga o
                  fluxo, mesmo sem estar nesta lista.
                </Typography>
              </FormControl>

              <TextField
                label="Vídeo de apresentação"
                value={form.videoUrl}
                onChange={(e) => setForm({ ...form, videoUrl: e.target.value })}
                fullWidth
                size="small"
                multiline
                maxRows={3}
                inputProps={{ maxLength: 1200 }}
                InputLabelProps={{ shrink: true }}
                placeholder='https://www.youtube.com/watch?v=...  ou  <iframe src="https://www.youtube.com/embed/..."></iframe>'
                helperText="Cole o endereço do vídeo ou o código de incorporação do YouTube, Vimeo ou Loom. Ao salvar, o campo passa a mostrar o endereço de incorporação."
              />

              {/* Anexos: só em edição — o upload precisa do id do fluxo já criado. */}
              {form.id && (
                <>
                  <Divider />
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      <Typography sx={{ fontWeight: 600, color: colors.textPrimary }}>
                        Documentos de apoio
                      </Typography>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept={ATTACHMENT_ACCEPT}
                        hidden
                        onChange={(e) => {
                          uploadAttachment(e.target.files?.[0] ?? null);
                          e.target.value = "";
                        }}
                      />
                      <Button
                        startIcon={<AttachFile />}
                        onClick={() => fileInputRef.current?.click()}
                        disabled={saving || attachments.length >= ATTACHMENT_MAX_COUNT}
                        variant="outlined"
                        size="small"
                        sx={{ textTransform: "none", color: colors.primary, borderColor: colors.primary }}
                      >
                        Anexar arquivo
                      </Button>
                    </Box>
                    <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                      Até {ATTACHMENT_MAX_COUNT} arquivos de 10 MB (pdf, imagem, texto, planilha,
                      documento ou apresentação).
                    </Typography>

                    {loadingAttachments ? (
                      <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                        <CircularProgress size={24} sx={{ color: colors.primary }} />
                      </Box>
                    ) : attachments.length === 0 ? (
                      <Box
                        sx={{
                          textAlign: "center",
                          py: 3,
                          color: colors.textSecondary,
                          border: "1px dashed #ced4da",
                          borderRadius: "8px",
                        }}
                      >
                        <InsertDriveFile sx={{ fontSize: 36, color: "#ced4da", mb: 0.5 }} />
                        <Typography variant="body2" sx={{ color: colors.textPrimary }}>
                          Nenhum documento anexado
                        </Typography>
                        <Typography variant="caption">
                          Anexe modelos, checklists ou manuais que apoiam o processo.
                        </Typography>
                      </Box>
                    ) : (
                      attachments.map((attachment) => (
                        <Box
                          key={attachment.id}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            p: 1,
                            border: "1px solid #e9ecef",
                            borderRadius: "8px",
                          }}
                        >
                          <InsertDriveFile sx={{ color: colors.textSecondary, flexShrink: 0 }} />
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                              sx={{ color: colors.textPrimary, wordBreak: "break-word", lineHeight: 1.25 }}
                            >
                              {attachment.fileName}
                            </Typography>
                            <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                              {formatFileSize(attachment.sizeBytes)}
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={() => downloadAttachment(attachment)}
                            title="Baixar"
                            sx={{ color: colors.primary, flexShrink: 0 }}
                          >
                            <Download sx={{ fontSize: "1.1rem" }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() =>
                              askConfirm({
                                title: "Excluir anexo",
                                message: `Excluir o arquivo "${attachment.fileName}"? Ele deixa de aparecer para quem consulta o processo.`,
                                confirmText: "Excluir",
                                run: () => removeAttachment(attachment),
                              })
                            }
                            title="Excluir"
                            sx={{ color: colors.error, flexShrink: 0 }}
                          >
                            <DeleteOutline sx={{ fontSize: "1.1rem" }} />
                          </IconButton>
                        </Box>
                      ))
                    )}
                  </Box>
                  <Divider />
                </>
              )}

              <Typography sx={{ fontSize: "0.8rem", color: colors.textSecondary }}>
                {form.id
                  ? "Alterar estes dados não muda as versões já publicadas."
                  : "O fluxo nasce com a versão 1 em rascunho — ao criar, você vai direto para o desenho das etapas."}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: "1rem 1.25rem" }}>
          <Button
            onClick={() => setForm(null)}
            variant="outlined"
            sx={{ textTransform: "none", color: "#6c757d", borderColor: "#6c757d" }}
          >
            Cancelar
          </Button>
          <Button
            onClick={save}
            variant="contained"
            disabled={saving}
            sx={{ textTransform: "none", backgroundColor: colors.primary }}
          >
            {saving ? "Salvando..." : form?.id ? "Salvar" : "Criar fluxo"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Histórico de versões do fluxo */}
      <Dialog
        open={versionsOf !== null}
        onClose={() => setVersionsOf(null)}
        maxWidth="md"
        fullWidth
        fullScreen={fullScreenDialog}
        PaperProps={{
          sx: {
            borderRadius: fullScreenDialog ? 0 : "12px",
            position: "relative",
            overflow: "hidden",
          },
        }}
      >
        <SavingOverlay open={saving} message="Processando..." />
        <DialogTitle
          sx={{
            backgroundColor: colors.primary,
            color: colors.white,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
            padding: "1rem 1.25rem",
          }}
        >
          <Typography
            component="h3"
            sx={{
              fontSize: "1.1rem",
              fontWeight: typography.fontWeight.semibold,
              margin: 0,
              minWidth: 0,
              wordBreak: "break-word",
            }}
          >
            Versões · {versionsOf?.name}
          </Typography>
          <IconButton
            onClick={() => setVersionsOf(null)}
            sx={{ color: colors.white, padding: "0.25rem", flexShrink: 0 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "1.25rem !important", paddingTop: "1.5rem !important" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Typography variant="body2" sx={{ color: colors.textSecondary, flex: "1 1 200px" }}>
                A versão publicada é imutável. Para alterar o processo, crie uma nova versão a partir da
                vigente.
              </Typography>
              <Tooltip
                title={hasDraft ? "Já existe um rascunho aberto neste fluxo" : "Clona a versão vigente"}
              >
                <span>
                  <Button
                    startIcon={<Add />}
                    onClick={createVersion}
                    disabled={saving || loadingVersions || hasDraft}
                    variant="outlined"
                    sx={{ textTransform: "none", color: colors.primary, borderColor: colors.primary }}
                  >
                    Nova versão
                  </Button>
                </span>
              </Tooltip>
            </Box>

            {loadingVersions || versions === null ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress sx={{ color: colors.primary }} />
              </Box>
            ) : versions.length === 0 ? (
              <Box
                sx={{
                  textAlign: "center",
                  py: 4,
                  color: colors.textSecondary,
                  border: "1px dashed #ced4da",
                  borderRadius: "8px",
                }}
              >
                <History sx={{ fontSize: 40, color: "#ced4da", mb: 1 }} />
                <Typography variant="body2" sx={{ color: colors.textPrimary }}>
                  Nenhuma versão neste fluxo
                </Typography>
                <Typography variant="caption">Crie a primeira versão em "Nova versão".</Typography>
              </Box>
            ) : (
              versions
                .slice()
                .sort((a, b) => b.versionNumber - a.versionNumber)
                .map((version) => (
                  <Paper
                    key={version.id}
                    elevation={0}
                    sx={{
                      border: "1px solid #e9ecef",
                      borderRadius: "8px",
                      p: 1.5,
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      alignItems: { xs: "stretch", sm: "center" },
                      gap: 1.5,
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <Typography sx={{ fontWeight: 600, color: colors.textPrimary }}>
                          v{version.versionNumber}
                        </Typography>
                        <Chip
                          label={VERSION_STATUS_LABELS[version.status] ?? version.status}
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: "0.7rem",
                            color: "#fff",
                            bgcolor: VERSION_STATUS_COLORS[version.status] ?? "#6c757d",
                          }}
                        />
                        <Chip
                          label={`${version.stepCount} ${version.stepCount === 1 ? "etapa" : "etapas"}`}
                          size="small"
                          variant="outlined"
                          sx={{ height: 22, fontSize: "0.7rem" }}
                        />
                        {version.clonedFromVersionNumber && (
                          <Chip
                            label={`clonada da v${version.clonedFromVersionNumber}`}
                            size="small"
                            variant="outlined"
                            sx={{ height: 22, fontSize: "0.7rem" }}
                          />
                        )}
                      </Box>
                      {version.notes && (
                        <Typography
                          variant="caption"
                          sx={{ display: "block", color: colors.textSecondary, wordBreak: "break-word", mt: 0.5 }}
                        >
                          {version.notes}
                        </Typography>
                      )}
                      <Typography variant="caption" sx={{ display: "block", color: colors.textSecondary }}>
                        Criada em {formatDate(version.createdAt)}
                        {version.publishedAt ? ` · Publicada em ${formatDate(version.publishedAt)}` : ""}
                        {version.retiredAt ? ` · Encerrada em ${formatDate(version.retiredAt)}` : ""}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        flexWrap: "wrap",
                        justifyContent: { xs: "flex-start", sm: "flex-end" },
                        flexShrink: 0,
                      }}
                    >
                      {version.status === "draft" ? (
                        <>
                          <IconButton
                            size="small"
                            onClick={() => goToEditor(version.id)}
                            title="Editar rascunho"
                            sx={{
                              backgroundColor: "#03B4C6",
                              color: "white",
                              width: 32,
                              height: 32,
                              "&:hover": { backgroundColor: "#029AAB" },
                            }}
                          >
                            <Edit sx={{ fontSize: "1rem" }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() =>
                              askConfirm({
                                title: "Publicar versão",
                                message: `Publicar a v${version.versionNumber}? A partir daí ela fica imutável — para mudar, será preciso criar uma nova versão.`,
                                confirmText: "Publicar",
                                run: () =>
                                  runVersionAction(
                                    () => workflowsService.publishVersion(version.id),
                                    "Versão publicada."
                                  ),
                              })
                            }
                            title="Publicar"
                            sx={{
                              backgroundColor: "#28a745",
                              color: "white",
                              width: 32,
                              height: 32,
                              "&:hover": { backgroundColor: "#218838" },
                            }}
                          >
                            <Publish sx={{ fontSize: "1rem" }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() =>
                              askConfirm({
                                title: "Descartar rascunho",
                                message: `Descartar a v${version.versionNumber}? O desenho em edição é perdido.`,
                                confirmText: "Descartar",
                                run: () =>
                                  runVersionAction(
                                    () => workflowsService.removeVersion(version.id),
                                    "Rascunho descartado."
                                  ),
                              })
                            }
                            title="Descartar rascunho"
                            sx={{
                              backgroundColor: "#dc3545",
                              color: "white",
                              width: 32,
                              height: 32,
                              "&:hover": { backgroundColor: "#c82333" },
                            }}
                          >
                            <DeleteOutline sx={{ fontSize: "1rem" }} />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <IconButton
                            size="small"
                            onClick={() => goToEditor(version.id)}
                            title="Ver desenho"
                            sx={{
                              backgroundColor: "#6c757d",
                              color: "white",
                              width: 32,
                              height: 32,
                              "&:hover": { backgroundColor: "#5a6268" },
                            }}
                          >
                            <Visibility sx={{ fontSize: "1rem" }} />
                          </IconButton>
                          {version.status === "published" && (
                            <>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  askConfirm({
                                    title: "Despublicar versão",
                                    message: `Voltar a v${version.versionNumber} para rascunho? Só use antes de o processo entrar em uso.`,
                                    confirmText: "Despublicar",
                                    run: () =>
                                      runVersionAction(
                                        () => workflowsService.unpublishVersion(version.id),
                                        "Versão voltou para rascunho."
                                      ),
                                  })
                                }
                                title="Despublicar"
                                sx={{
                                  backgroundColor: "#fd7e14",
                                  color: "white",
                                  width: 32,
                                  height: 32,
                                  "&:hover": { backgroundColor: "#e8690b" },
                                }}
                              >
                                <Undo sx={{ fontSize: "1rem" }} />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  askConfirm({
                                    title: "Encerrar versão",
                                    message: `Encerrar a v${version.versionNumber}? Ela deixa de valer para novas aplicações do fluxo.`,
                                    confirmText: "Encerrar",
                                    run: () =>
                                      runVersionAction(
                                        () => workflowsService.retireVersion(version.id),
                                        "Versão encerrada."
                                      ),
                                  })
                                }
                                title="Encerrar"
                                sx={{
                                  backgroundColor: "#495057",
                                  color: "white",
                                  width: 32,
                                  height: 32,
                                  "&:hover": { backgroundColor: "#343a40" },
                                }}
                              >
                                <Block sx={{ fontSize: "1rem" }} />
                              </IconButton>
                            </>
                          )}
                        </>
                      )}
                    </Box>
                  </Paper>
                ))
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: "1rem 1.25rem" }}>
          <Button
            onClick={() => setVersionsOf(null)}
            variant="outlined"
            sx={{ textTransform: "none", color: "#6c757d", borderColor: "#6c757d" }}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmModal
        isOpen={deleteTarget !== null}
        title="Excluir fluxo"
        message={`Excluir o fluxo "${deleteTarget?.name ?? ""}"? Só é possível excluir fluxos que nunca tiveram versão publicada — os demais devem ser inativados.`}
        type="warning"
        confirmButtonText="Excluir"
        cancelButtonText="Cancelar"
        isProcessing={saving}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <ConfirmModal
        isOpen={pendingAction !== null}
        title={pendingAction?.title ?? ""}
        message={pendingAction?.message ?? ""}
        type="warning"
        confirmButtonText={pendingAction?.confirmText ?? "Confirmar"}
        cancelButtonText="Cancelar"
        isProcessing={saving}
        onConfirm={runPendingAction}
        onCancel={() => setPendingAction(null)}
      />

      <FooterInternal className="login-footer-component" />
      <Toast message={toast.message} type={toast.type} isVisible={toast.isVisible} onClose={hideToast} />
    </Box>
  );
};

export default Workflows;
